import { Badge, Button, Empty, List, Modal, Popover, Tabs, Spin, Tooltip, Typography, notification as antNotification } from "antd";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { IoNotificationsOutline } from "react-icons/io5";
import {
    WarningFilled,
    ExclamationCircleFilled,
    InfoCircleFilled,
    ClockCircleOutlined,
    EnvironmentOutlined,
    AppstoreOutlined,
    RightOutlined,
    DownOutlined,
    DisconnectOutlined,
} from "@ant-design/icons";
import { useMutation } from "react-query";
import axios from "axios";
import { AppContext } from "../AppContext";
import tr from "../i18n";
import { useAccess } from "../utils/UseAccess";
import { ROLES } from "../utils/AccessLevels";
import { AccessGate } from "./AccessGate";
import { useNavigate } from "react-router-dom";
import {
    resolveNotificationDevices,
    groupDevicesByCause,
    summarizeCauses,
    formatCauseDetail,
} from "../utils/NoWateringCauses";

const { Text, Paragraph } = Typography;

const RESOLVE_ALARM_URL =
    process.env.REACT_APP_PLANNER_API_END_POINT + "/device/resolve-alarm";
const GARDENS_MAP_PATH = "/control-center/gardens";
const ALARMS_ROLE = ROLES.FIELD_MANAGER;

// Visual identity per notification type.
const TYPE_CONFIG = {
    no_watering: {
        color: "#ff4d4f",
        soft: "#fff1f0",
        border: "#ffccc7",
        icon: WarningFilled,
        label: "No Watering",
    },
    low_watering: {
        color: "#fa8c16",
        soft: "#fff7e6",
        border: "#ffd591",
        icon: ExclamationCircleFilled,
        label: "Low Watering",
    },
    default: {
        color: "#1677ff",
        soft: "#f0f7ff",
        border: "#bae0ff",
        icon: InfoCircleFilled,
        label: "Info",
    },
};

const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.default;

// Only for pre-cause records: neutral grey, connectivity rather than severity.
const LEGACY_OFFLINE_ACCENT = "#8c8c8c";

// Lifted out of the component so the chip tooltips can format timestamps too.
const formatDate = (d) => {
    if (!d) return "";
    const locale = localStorage.getItem("locale") || "en";
    return new Date(d).toLocaleString(locale, {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

function Chip({ icon, label, accent, onClick, tooltip, disabled }) {
    const button = (
        <button
            type="button"
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                background: disabled ? "#fafafa" : "#fff",
                border: "1px solid #e8e8e8",
                borderRadius: 8,
                cursor: disabled ? "default" : "pointer",
                fontSize: 13,
                color: disabled ? "#bfbfbf" : "#262626",
                maxWidth: "100%",
                transition: "border-color 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => {
                if (disabled) return;
                e.currentTarget.style.borderColor = accent;
                e.currentTarget.style.boxShadow = `0 1px 4px ${accent}22`;
            }}
            onMouseLeave={(e) => {
                if (disabled) return;
                e.currentTarget.style.borderColor = "#e8e8e8";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            {icon}
            <span
                style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </span>
            {!disabled && <RightOutlined style={{ fontSize: 10, color: "#bfbfbf" }} />}
        </button>
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
}

function ChipGroup({ label, hint, items, accent, getKey, getLabel, getTooltip, getDisabled, icon, onClick }) {
    const COLLAPSE_THRESHOLD = 3; // show this many before collapsing
    const [expanded, setExpanded] = useState(false);

    const groupLabelStyle = {
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "#8c8c8c",
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    };

    const overflowing = items.length > COLLAPSE_THRESHOLD;
    const visible = expanded ? items : items.slice(0, COLLAPSE_THRESHOLD);

    const heading = (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {icon ? icon(accent) : null}
            <span style={{ color: accent }}>{label}</span>
            <span style={{ color: "#bfbfbf", fontWeight: 500 }}>{items.length}</span>
        </span>
    );

    return (
        <div>
            <div style={groupLabelStyle}>
                {hint ? <Tooltip title={hint}>{heading}</Tooltip> : heading}
                {overflowing && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.2,
                            textTransform: "none",
                            color: accent,
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        {expanded ? tr("Show less") : `${tr("Show all")}`}
                        <DownOutlined
                            style={{
                                fontSize: 9,
                                transform: expanded ? "rotate(180deg)" : "none",
                                transition: "transform 0.15s",
                            }}
                        />
                    </button>
                )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {visible.map((item) => (
                    <Chip
                        key={getKey(item)}
                        accent={accent}
                        onClick={() => onClick(item)}
                        icon={icon(accent)}
                        label={getLabel(item)}
                        tooltip={getTooltip ? getTooltip(item) : null}
                        disabled={getDisabled ? getDisabled(item) : false}
                    />
                ))}
            </div>
        </div>
    );
}

/**
 * Triage bar: the shape of the problem before you expand anything. Counts are
 * read straight off the notification doc, so this renders instantly - the chip
 * groups below wait on the /properties request for device names.
 */
function CauseSummary({ summary, style, compact = false }) {
    if (!summary.length) return null;

    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, ...style }}>
            {summary.map(({ cause, count, config }) => {
                const Icon = config.icon;
                return (
                    <Tooltip key={cause} title={config.hint()}>
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                padding: compact ? "1px 7px" : "3px 9px",
                                background: config.soft,
                                border: `1px solid ${config.border}`,
                                borderRadius: 999,
                                fontSize: compact ? 11 : 12,
                                color: config.color,
                                fontWeight: 600,
                                lineHeight: 1.6,
                            }}
                        >
                            <Icon style={{ fontSize: compact ? 10 : 12 }} />
                            {compact ? null : <span style={{ fontWeight: 500 }}>{config.label()}</span>}
                            <span>{count}</span>
                        </span>
                    </Tooltip>
                );
            })}
        </div>
    );
}

function RelatedSection({
    loading,
    props,
    notification,
    accent,
    type,
    onGardenClick,
    onDeviceClick,
}) {
    const isNoWatering = type === "no_watering";
    // Records written before this feature have no cause data. Rendering them
    // through the cause path would label every device "Cause not determined",
    // which reads like a bug rather than missing history - so they keep the
    // original two-group layout.
    const hasCauses = Boolean(notification?.nonWateringDevices?.length);
    const useCauseGroups = isNoWatering && hasCauses;

    const causeGroups = useMemo(() => {
        if (!useCauseGroups) return [];
        return groupDevicesByCause(resolveNotificationDevices(props, notification));
    }, [useCauseGroups, props, notification]);

    if (loading) {
        return (
            <div style={{ marginTop: 16, textAlign: "center", padding: "12px 0" }}>
                <Spin size="small" />
            </div>
        );
    }

    const gardens = props?.gardens || [];
    const devices = props?.devices || [];
    const offlineDevices = props?.offlineDevices || [];

    if (!gardens.length && !devices.length && !offlineDevices.length) return null;

    const deviceKey = (d) => d._id || d.deviceSerial || d.id;
    const deviceIcon = (Icon) => (color) => <Icon style={{ color, fontSize: 13 }} />;

    return (
        <div style={{ marginTop: 16, borderTop: "1px solid #f0f0f0", paddingTop: 14 }}>
            {gardens.length > 0 && (
                <div style={{ marginBottom: (devices.length || offlineDevices.length) ? 14 : 0 }}>
                    <ChipGroup
                        label={tr("Gardens")}
                        items={gardens}
                        accent={accent}
                        getKey={(g) => g.id}
                        getLabel={(g) => g.name}
                        icon={(c) => <EnvironmentOutlined style={{ color: c, fontSize: 13 }} />}
                        onClick={onGardenClick}
                    />
                </div>
            )}

            {useCauseGroups ? (
                causeGroups.map(({ cause, items, config }, index) => (
                    <div
                        key={cause}
                        style={{ marginBottom: index < causeGroups.length - 1 ? 14 : 0 }}
                    >
                        <ChipGroup
                            label={config.label()}
                            hint={config.hint()}
                            items={items}
                            accent={config.color}
                            getKey={deviceKey}
                            getLabel={(d) => d.name || d.deviceSerial}
                            getDisabled={(d) => !d.id}
                            getTooltip={(d) =>
                                d.deleted
                                    ? tr("This device has been removed since the notification was sent.")
                                    : formatCauseDetail(cause, d.meta, formatDate) || config.hint()
                            }
                            icon={deviceIcon(config.icon)}
                            onClick={onDeviceClick}
                        />
                    </div>
                ))
            ) : (
                <>
                    {devices.length > 0 && (
                        <div style={{ marginBottom: offlineDevices.length ? 14 : 0 }}>
                            <ChipGroup
                                label={isNoWatering ? tr("Devices that did not water") : tr("Devices")}
                                items={devices}
                                accent={accent}
                                getKey={deviceKey}
                                getLabel={(d) => d.name}
                                icon={(c) => <AppstoreOutlined style={{ color: c, fontSize: 13 }} />}
                                onClick={onDeviceClick}
                            />
                        </div>
                    )}

                    {offlineDevices.length > 0 && (
                        <ChipGroup
                            label={tr("Offline devices")}
                            items={offlineDevices}
                            accent={LEGACY_OFFLINE_ACCENT}
                            getKey={deviceKey}
                            getLabel={(d) => d.name}
                            icon={(c) => <DisconnectOutlined style={{ color: c, fontSize: 13 }} />}
                            onClick={onDeviceClick}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default function NotificationPanel() {
    const {
        alarms,
        fetchAlarms,
        notifications,
        notificationsPagination,
        unreadNotificationCount,
        loadingNotifications,
        fetchNotifications,
        loadMoreNotifications,
        markNotificationRead,
        fetchNotificationProperties
    } = useContext(AppContext);
    const navigate = useNavigate();

    const { can, isAuthReady } = useAccess();
    const canSeeAlarms = isAuthReady && can(ALARMS_ROLE);

    const [popoverVisible, setPopoverVisible] = useState(false);
    const [activeTab, setActiveTab] = useState("alarms");

    // Alarm resolve confirmation
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedAlarmId, setSelectedAlarmId] = useState(null);

    // Notification detail
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [notifProps, setNotifProps] = useState(null);
    const [loadingProps, setLoadingProps] = useState(false);

    const listRef = useRef(null);

    const alarmCount = alarms?.length ?? 0;
    const hasAlarms = canSeeAlarms && alarmCount > 0;

    // Badge: alarms take priority (red) only for users who can see them.
    // Everyone else falls back to the blue unread-notification badge.
    const badgeCount = hasAlarms ? alarmCount : unreadNotificationCount;
    const badgeColor = hasAlarms ? "#ff4d4f" : "#1677ff";

    // When the popover opens, refresh both sources and land on the right tab.
    const handleOpenChange = (visible) => {
        if (!visible && (selectedNotification || showConfirmModal)) return;
        setPopoverVisible(visible);
        if (visible) {
            if (canSeeAlarms) fetchAlarms({ resolved: false });
            fetchNotifications({ reset: true });
            setActiveTab(hasAlarms ? "alarms" : "notifications");
        }
    };

    const handleGardenClick = (garden) => {
        // Close the detail modal and the popover, then focus the garden on the map.
        setSelectedNotification(null);
        setNotifProps(null);
        setPopoverVisible(false);
        navigate(`${GARDENS_MAP_PATH}?selectedGardenId=${encodeURIComponent(garden.id)}`);
    };

    const handleDeviceClick = (device) => {
        if (!device.id) {
            console.warn("Device has no routable id, skipping navigation", device._id);
            return;
        }
        setSelectedNotification(null);
        setNotifProps(null);
        setPopoverVisible(false);
        navigate(`${GARDENS_MAP_PATH}?device_id=${encodeURIComponent(device.id)}&show_map=true`);
    };

    // Keep the default tab in sync if alarms arrive/clear while closed.
    useEffect(() => {
        if (!popoverVisible) setActiveTab(hasAlarms ? "alarms" : "notifications");
    }, [hasAlarms, popoverVisible]);

    // ---- Alarm resolve ----
    const resolveAlarm = useMutation(
        (alarmId) => axios.put(RESOLVE_ALARM_URL, alarmId),
        {
            onSuccess: () => {
                setShowConfirmModal(false);
                setSelectedAlarmId(null);
                fetchAlarms({ resolved: false });
            },
            onError: () => {
                antNotification.error({
                    message: tr("Alarm Resolution Failed"),
                    description: tr("Alarm resolution failed"),
                });
                fetchAlarms({ resolved: false });
            },
        }
    );

    const handleConfirmResolve = () => {
        resolveAlarm.mutate({ alarmId: selectedAlarmId });
    };

    // ---- Notification detail + read ----
    const openNotification = async (item) => {
        setSelectedNotification(item);
        setNotifProps(null);
        setLoadingProps(true);

        // Mark read (existing behavior)
        if (!item.isRead) {
            try {
                await markNotificationRead(item._id);
                setSelectedNotification((cur) =>
                    cur && cur._id === item._id ? { ...cur, isRead: true } : cur
                );
            } catch {
                // markNotificationRead logs; detail still opens.
            }
        }

        // Load garden/device summaries
        try {
            const props = await fetchNotificationProperties(item._id);
            setNotifProps(props);
        } catch (error) {
            console.error("Error loading notification properties:", error);
            setNotifProps({ gardens: [], devices: [], offlineDevices: [] });
        } finally {
            setLoadingProps(false);
        }
    };

    // ---- Infinite scroll ----
    const canLoadMore =
        notificationsPagination &&
        notificationsPagination.page < notificationsPagination.pages;

    const handleScroll = (e) => {
        if (activeTab !== "notifications" || !canLoadMore || loadingNotifications) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 40) {
            loadMoreNotifications();
        }
    };

    const alarmsTab = (
        <div
            style={{ maxHeight: 450, overflowY: "auto", padding: "12px" }}
        >
            {hasAlarms ? (
                <List
                    dataSource={alarms}
                    renderItem={(alarm) => (
                        <List.Item
                            style={{
                                padding: "16px",
                                backgroundColor: "#fffafa",
                                border: "1px solid #ffccc7",
                                borderRadius: "12px",
                                marginBottom: "12px",
                                display: "block",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                                <div style={{ paddingTop: "6px" }}>
                                    <div
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            background: "#ff4d4f",
                                            boxShadow: "0 0 0 2px #fff, 0 0 8px 2px rgba(255, 77, 79, 0.5)",
                                        }}
                                    />
                                </div>

                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text strong style={{ fontSize: 14, display: "block" }}>
                                            {alarm.deviceName || tr("Unknown Device")}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {formatDate(alarm.createdAt)}
                                        </Text>
                                    </div>

                                    <Text danger style={{ fontSize: 13, fontWeight: 600, display: "block" }}>
                                        {tr("Water Consumption Alarm")}
                                    </Text>

                                    {alarm.gardenName && (
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
                                            <span style={{ opacity: 0.7 }}>{tr("Location")}:</span> {alarm.gardenName}
                                        </Text>
                                    )}
                                </div>

                                {!alarm.resolved && (
                                    <div style={{ alignSelf: "center" }}>
                                        <Button
                                            danger
                                            type="primary"
                                            size="small"
                                            style={{
                                                borderRadius: "6px",
                                                fontWeight: 600,
                                                fontSize: "11px",
                                                height: "30px",
                                                padding: "0 12px",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedAlarmId(alarm._id);
                                                setShowConfirmModal(true);
                                            }}
                                        >
                                            {tr("Resolve")}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </List.Item>
                    )}
                />
            ) : (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={tr("No active alarms")}
                    style={{ padding: "24px 0" }}
                />
            )}
        </div>
    );

    const notificationsTab = (
        <div
            ref={listRef}
            onScroll={handleScroll}
            style={{ maxHeight: 450, overflowY: "auto", padding: "12px" }}
        >
            {notifications?.length ? (
                <>
                    <List
                        dataSource={notifications}
                        renderItem={(item) => {
                            const summary = summarizeCauses(item.nonWateringDevices);
                            return (
                                <List.Item
                                    onClick={() => openNotification(item)}
                                    style={{
                                        padding: "14px 16px",
                                        backgroundColor: item.isRead ? "#fafafa" : "#f0f7ff",
                                        border: `1px solid ${item.isRead ? "#f0f0f0" : "#bae0ff"}`,
                                        borderRadius: "12px",
                                        marginBottom: "12px",
                                        display: "block",
                                        cursor: "pointer",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                                        {!item.isRead && (
                                            <div style={{ paddingTop: 6 }}>
                                                <div
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        background: "#1677ff",
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Text
                                                strong={!item.isRead}
                                                style={{ fontSize: 14, display: "block" }}
                                            >
                                                {item.title}
                                            </Text>
                                            <Paragraph
                                                type="secondary"
                                                ellipsis={{ rows: 2 }}
                                                style={{ fontSize: 12, marginBottom: 4, marginTop: 2 }}
                                            >
                                                {item.body}
                                            </Paragraph>

                                            {/* Triage without opening: cause counts, compact. */}
                                            <CauseSummary
                                                summary={summary}
                                                compact
                                                style={{ marginBottom: 6 }}
                                            />

                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {formatDate(item.sentAt || item.createdAt)}
                                            </Text>
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                    {loadingNotifications && (
                        <div style={{ textAlign: "center", padding: "8px 0" }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {tr("Loading...")}
                            </Text>
                        </div>
                    )}
                </>
            ) : (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={tr("No notifications")}
                    style={{ padding: "24px 0" }}
                />
            )}
        </div>
    );

    const tabItems = useMemo(() => {
        const items = [];

        if (canSeeAlarms) {
            items.push({
                key: "alarms",
                label: (
                    <span>
                        {tr("Alarms")}
                        {hasAlarms && (
                            <Badge
                                count={alarmCount}
                                size="small"
                                style={{ backgroundColor: "#ff4d4f", marginLeft: 8 }}
                            />
                        )}
                    </span>
                ),
                children: alarmsTab,
            });
        }

        items.push({
            key: "notifications",
            label: (
                <span>
                    {tr("Notifications")}
                    {unreadNotificationCount > 0 && (
                        <Badge
                            count={unreadNotificationCount}
                            size="small"
                            style={{ backgroundColor: "#1677ff", marginLeft: 8 }}
                        />
                    )}
                </span>
            ),
            children: notificationsTab,
        });

        return items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSeeAlarms, alarms, notifications, hasAlarms, alarmCount, unreadNotificationCount, loadingNotifications, activeTab]);

    const content = (
        <div style={{ width: 400 }}>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                tabBarStyle={{ padding: "0 16px", marginBottom: 0 }}
            />
        </div>
    );

    return (
        <>
            <Popover
                content={content}
                trigger="click"
                open={popoverVisible}
                onOpenChange={handleOpenChange}
                styles={{ body: { padding: 0 } }}
            >
                <Badge
                    count={badgeCount > 99 ? "99+" : badgeCount}
                    size="small"
                    color={badgeColor}
                    style={{ marginRight: 21, marginTop: 5 }}
                >
                    <Button
                        type="link"
                        icon={<IoNotificationsOutline size={20} color="white" />}
                        style={{ marginRight: 12 }}
                    />
                </Badge>
            </Popover>

            {/* Resolve alarm confirmation */}
            <Modal
                title={tr("Resolve Alarm")}
                open={showConfirmModal}
                onOk={handleConfirmResolve}
                onCancel={() => setShowConfirmModal(false)}
                okText={tr("Resolve")}
                cancelText={tr("Cancel")}
                okButtonProps={{ danger: true }}
                confirmLoading={resolveAlarm.isLoading}
                zIndex={100000}
            >
                <Text>{tr("Are you sure you want to resolve this alarm?")}</Text>
            </Modal>

            {/* Notification detail */}
            <Modal
                open={!!selectedNotification}
                closable={false}
                onCancel={() => {
                    setSelectedNotification(null);
                    setNotifProps(null);
                }}
                title={null}
                footer={[
                    <Button key="close" onClick={() => setSelectedNotification(null)}>
                        {tr("Close")}
                    </Button>,
                ]}
                zIndex={100000}
                styles={{ body: { paddingTop: 8 } }}
            >
                {selectedNotification && (() => {
                    const cfg = getTypeConfig(selectedNotification.type);
                    const Icon = cfg.icon;
                    const summary = summarizeCauses(selectedNotification.nonWateringDevices);
                    return (
                        <div>
                            {/* Typed header */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: "14px 16px",
                                    background: cfg.soft,
                                    border: `1px solid ${cfg.border}`,
                                    borderRadius: 12,
                                    marginBottom: 16,
                                }}
                            >
                                <div
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 10,
                                        background: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Icon style={{ color: cfg.color, fontSize: 20 }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <Text
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            letterSpacing: 0.4,
                                            textTransform: "uppercase",
                                            color: cfg.color,
                                            display: "block",
                                        }}
                                    >
                                        {tr(cfg.label)}
                                    </Text>
                                    <Text strong style={{ fontSize: 16, display: "block", lineHeight: 1.3 }}>
                                        {selectedNotification.title}
                                    </Text>
                                </div>
                            </div>

                            {/* Metadata strip */}
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        {formatDate(selectedNotification.sentAt || selectedNotification.createdAt)}
                                    </Text>
                                </div>
                            </div>

                            {/* Why it happened - renders immediately, before /properties resolves. */}
                            {summary.length > 0 && (
                                <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 14, marginBottom: 14 }}>
                                    <div
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            letterSpacing: 0.4,
                                            textTransform: "uppercase",
                                            color: "#8c8c8c",
                                            marginBottom: 8,
                                        }}
                                    >
                                        {tr("Why watering did not happen")}
                                    </div>
                                    <CauseSummary summary={summary} />
                                </div>
                            )}

                            {/* Body */}
                            <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 14 }}>
                                <Paragraph style={{ whiteSpace: "pre-wrap", fontSize: 14, marginBottom: 0, color: "#262626" }}>
                                    {selectedNotification.body}
                                </Paragraph>
                            </div>

                            {/* Related gardens & devices */}
                            <RelatedSection
                                loading={loadingProps}
                                props={notifProps}
                                notification={selectedNotification}
                                accent={cfg.color}
                                type={selectedNotification.type}
                                onGardenClick={handleGardenClick}
                                onDeviceClick={handleDeviceClick}
                            />
                        </div>
                    );
                })()}
            </Modal>
        </>
    );
}
